import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category?type=movie' },
  { label: '电视剧', to: '/category?type=tv' },
  { label: '资讯', to: '#news' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === '#news') return false;
    const [path, query] = to.split('?');
    if (path !== location.pathname) return false;
    if (!query) return !location.search || location.pathname !== '/category';
    const targetType = new URLSearchParams(query).get('type');
    const currentType = new URLSearchParams(location.search).get('type') ?? 'movie';
    return targetType === currentType;
  };

  const goNews = () => {
    if (isHome) {
      document.getElementById('news')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/');
      window.setTimeout(() => {
        document.getElementById('news')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 220);
    }
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword('');
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 border-b border-background-200 backdrop-blur-md'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-portal items-center gap-4 px-4 md:h-[72px] md:gap-6 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md text-lg ${
              transparent ? 'bg-background-50/15 text-accent-300' : 'bg-primary-600 text-background-50'
            }`}
          >
            <i className="ri-leaf-fill" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-wide ${
                transparent ? 'text-background-50' : 'text-primary-700'
              }`}
            >
              青青草
            </span>
            <span
              className={`mt-1 text-[10px] font-medium uppercase tracking-[0.22em] ${
                transparent ? 'text-background-200' : 'text-foreground-400'
              }`}
            >
              Qingqingcao
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.to);
            if (item.to === '#news') {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={goNews}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    transparent
                      ? 'text-background-100 hover:bg-background-50/10 hover:text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-primary-700'
                  }`}
                >
                  {item.label}
                </button>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.to}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  active
                    ? transparent
                      ? 'bg-background-50/15 text-background-50'
                      : 'bg-primary-50 text-primary-700'
                    : transparent
                      ? 'text-background-100 hover:bg-background-50/10 hover:text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-primary-700'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden md:block">
          <div
            className={`flex h-10 w-56 items-center gap-2 rounded-full border px-3 transition-colors lg:w-72 ${
              transparent
                ? 'border-background-50/30 bg-background-50/10'
                : 'border-background-200 bg-background-100'
            }`}
          >
            <i
              className={`ri-search-line text-base ${
                transparent ? 'text-background-200' : 'text-foreground-400'
              }`}
            />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影、剧集、演员"
              className={`w-full bg-transparent text-sm outline-none ${
                transparent
                  ? 'text-background-50 placeholder:text-background-300'
                  : 'text-foreground-900 placeholder:text-foreground-400'
              }`}
              aria-label="全站搜索"
            />
            <button type="submit" className="cursor-pointer text-xs font-medium">
              <span className={transparent ? 'text-accent-300' : 'text-accent-600'}>搜索</span>
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border md:hidden ${
            transparent
              ? 'border-background-50/30 text-background-50'
              : 'border-background-200 text-foreground-800'
          }`}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-4">
            <div className="flex h-11 w-full items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影、剧集、演员"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="全站搜索"
              />
              <button type="submit" className="text-xs font-semibold text-accent-600">
                搜索
              </button>
            </div>
          </form>
          <div className="flex flex-col">
            {NAV_ITEMS.map((item) =>
              item.to === '#news' ? (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    goNews();
                  }}
                  className="cursor-pointer border-b border-background-100 py-3 text-left text-sm text-foreground-700"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className="cursor-pointer border-b border-background-100 py-3 text-sm text-foreground-700"
                >
                  {item.label}
                </Link>
              ),
            )}
          </div>
        </div>
      )}
    </header>
  );
}